import type { EmailDocumentTemplateResponseDto } from '@/apis/backend/generated/models/emailDocumentTemplateResponseDto';
import type { EmailDocumentTemplatesControllerListParams } from '@/apis/backend/generated/models/emailDocumentTemplatesControllerListParams';

import { paths } from '@/routes/paths';
import { VirtuosoGrid } from 'react-virtuoso';
import { useTranslation } from 'react-i18next';
import { useMemo, useState, useEffect, forwardRef } from 'react';
import { useOrganization } from '@/context/organization-context';
import { UpgradeModal } from '@/components/billing/UpgradeModal';
import { useNavigate, createFileRoute } from '@tanstack/react-router';
import { FEATURE_FLAGS, useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useDefaultSectionsForScope } from '@/lib/use-default-sections';
import { TemplateChatPreview } from '@/components/templates/TemplateChatPreview';
import { useBillingControllerGetQuota } from '@/apis/backend/generated/billing/billing';
import { TemplateDocumentPreview } from '@/components/templates/TemplateDocumentPreview';
import { LibraryTemplateFillDialog } from '@/components/templates/LibraryTemplateFillDialog';
import { useTemplatePreviewContext } from '@/components/templates/useTemplatePreviewContext';
import { LibraryChatTemplateFillDialog } from '@/components/templates/LibraryChatTemplateFillDialog';
import { EmailDocumentTemplatesControllerListSort } from '@/apis/backend/generated/models/emailDocumentTemplatesControllerListSort';
import { useEmailDocumentTemplatesControllerList } from '@/apis/backend/generated/email-document-templates/email-document-templates';
import {
  useEmailTemplateTaxonomyControllerListTags,
  useEmailTemplateTaxonomyControllerListCategories,
  useEmailTemplateTaxonomyControllerListIndustries,
} from '@/apis/backend/generated/email-template-taxonomy/email-template-taxonomy';

import { Clear as ClearIcon, Search as SearchIcon } from '@mui/icons-material';
import {
  Box,
  Card,
  Grid,
  Alert,
  Button,
  Select,
  MenuItem,
  Skeleton,
  TextField,
  InputLabel,
  Typography,
  CardContent,
  FormControl,
  CardActionArea,
  InputAdornment,
} from '@mui/material';

export const Route = createFileRoute('/_authed/dashboard/o/$orgId/c/$clientId/templates/library')({
  component: LibraryTemplatesPage,
});

function LibraryTemplatesPage() {
  const { t } = useTranslation('templates');
  const navigate = useNavigate();
  const { activeOrganization, activeClient } = useOrganization();

  const orgId = activeOrganization?.id || '';
  const clientId = activeClient?.id || '';
  const tenantReady = !!orgId && !!clientId;

  // Instantiating a library template mints a new email, which a brand without
  // the self-serve builder cannot do -- so the whole tab forwards to "My".
  // Keyed off `tenantReady` because the flag map arrives with the org and reads
  // false until then; the queries below wait on the same answer rather than
  // fetching a catalogue this brand is being sent away from.
  const selfServeEnabled = useFeatureFlag(FEATURE_FLAGS.SELF_SERVE_BUILDER);
  const libraryEnabled = tenantReady && selfServeEnabled;

  useEffect(() => {
    if (!tenantReady || selfServeEnabled) return;
    navigate({
      to: `${paths.dashboard.tenantRoot(orgId, clientId)}/templates/my`,
      replace: true,
    });
  }, [tenantReady, selfServeEnabled, navigate, orgId, clientId]);

  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [tagId, setTagId] = useState<string | null>(null);
  const [industryId, setIndustryId] = useState<string | null>(null);
  const [sort, setSort] = useState<EmailDocumentTemplatesControllerListSort>(
    EmailDocumentTemplatesControllerListSort.featured,
  );
  const [fillDialogTemplate, setFillDialogTemplate] = useState<EmailDocumentTemplateResponseDto | null>(null);
  const [chatDialogTemplate, setChatDialogTemplate] = useState<EmailDocumentTemplateResponseDto | null>(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  // Same quota that gates /dashboard/emails/create — library template
  // instantiation persists a user-created template too, so it consumes the
  // monthly limit. Over-quota users get the UpgradeModal instead of the
  // fill dialog.
  const { data: quota } = useBillingControllerGetQuota(orgId, undefined, {
    query: { enabled: !!orgId },
  });
  const quotaExceeded = !!quota && !quota.canGenerate;

  const handleTemplateClick = (tpl: EmailDocumentTemplateResponseDto) => {
    if (quotaExceeded) {
      setUpgradeOpen(true);
      return;
    }
    if (tpl.renderMode === 'chat') {
      setChatDialogTemplate(tpl);
      return;
    }
    setFillDialogTemplate(tpl);
  };

  const queryParams = useMemo<EmailDocumentTemplatesControllerListParams>(() => {
    const params: EmailDocumentTemplatesControllerListParams = { sort };
    if (search) params.search = search;
    if (categoryId) params.categoryId = categoryId;
    if (tagId) params.tagId = tagId;
    if (industryId) params.industryId = industryId;
    return params;
  }, [search, categoryId, tagId, industryId, sort]);

  const { data: libraryData, isLoading } = useEmailDocumentTemplatesControllerList(
    orgId,
    clientId,
    queryParams,
    {
      query: { enabled: libraryEnabled },
    },
  );

  const { data: categoriesData } = useEmailTemplateTaxonomyControllerListCategories(
    orgId,
    clientId,
    { query: { enabled: libraryEnabled } },
  );
  const { data: tagsData } = useEmailTemplateTaxonomyControllerListTags(orgId, clientId, {
    query: { enabled: libraryEnabled },
  });
  const { data: industriesData } = useEmailTemplateTaxonomyControllerListIndustries(
    orgId,
    clientId,
    { query: { enabled: libraryEnabled } },
  );

  const categories = categoriesData?.categories ?? [];
  const tags = tagsData?.tags ?? [];
  const industries = industriesData?.industries ?? [];

  const libraryPreviewCtx = useTemplatePreviewContext(orgId, clientId, true);
  const { defaultHeader, defaultFooter } = useDefaultSectionsForScope(orgId, clientId);

  const libraryTemplates = useMemo(() => {
    const list = libraryData?.templates ?? [];
    // Chat-first re-sort only applies in the curated default view. When the
    // user explicitly picked Newest / A-Z we respect the backend ordering as-is.
    if (sort !== EmailDocumentTemplatesControllerListSort.featured) return list;
    return [...list].sort((a, b) => {
      const aChat = a.renderMode === 'chat' ? 0 : 1;
      const bChat = b.renderMode === 'chat' ? 0 : 1;
      return aChat - bChat;
    });
  }, [libraryData?.templates, sort]);

  const hasActiveFilters =
    !!search ||
    !!categoryId ||
    !!tagId ||
    !!industryId ||
    sort !== EmailDocumentTemplatesControllerListSort.featured;

  const clearFilters = () => {
    setSearch('');
    setCategoryId(null);
    setTagId(null);
    setIndustryId(null);
    setSort(EmailDocumentTemplatesControllerListSort.featured);
  };

  // Nothing to show while the effect above forwards to "My": with the queries
  // held back, the grid would flash its empty state on the way out.
  if (tenantReady && !selfServeEnabled) return null;

  return (
    <>
      <Box sx={{ mb: 4, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder={t('search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: { xs: 0, sm: 260 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <Button size="small" onClick={() => setSearch('')} sx={{ minWidth: 0, p: 0.5 }}>
                  <ClearIcon fontSize="small" />
                </Button>
              </InputAdornment>
            ) : null,
          }}
        />
        <FilterSelect
          label={t('filters.category')}
          options={categories.map((c) => ({ id: c.id, label: c.displayName }))}
          value={categoryId}
          onChange={setCategoryId}
        />
        <FilterSelect
          label={t('filters.industry')}
          options={industries.map((i) => ({ id: i.id, label: i.displayName }))}
          value={industryId}
          onChange={setIndustryId}
        />
        <FilterSelect
          label={t('filters.tag')}
          options={tags.map((tag) => ({ id: tag.id, label: tag.displayName }))}
          value={tagId}
          onChange={setTagId}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="sort-label">{t('filters.sort')}</InputLabel>
          <Select
            labelId="sort-label"
            label={t('filters.sort')}
            value={sort}
            onChange={(e) =>
              setSort(e.target.value as EmailDocumentTemplatesControllerListSort)
            }
          >
            <MenuItem value={EmailDocumentTemplatesControllerListSort.featured}>
              {t('librarySort.featured')}
            </MenuItem>
            <MenuItem value={EmailDocumentTemplatesControllerListSort.newest}>
              {t('librarySort.newest')}
            </MenuItem>
            <MenuItem value={EmailDocumentTemplatesControllerListSort.name}>
              {t('librarySort.name')}
            </MenuItem>
          </Select>
        </FormControl>
        {hasActiveFilters && (
          <Button
            size="small"
            onClick={clearFilters}
            startIcon={<ClearIcon fontSize="small" />}
            sx={{ textTransform: 'none' }}
          >
            {t('filters.clear')}
          </Button>
        )}
      </Box>

      {isLoading && (
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={i}>
              <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
              <Skeleton variant="text" sx={{ mt: 1 }} />
              <Skeleton variant="text" width="60%" />
            </Grid>
          ))}
        </Grid>
      )}

      {!isLoading && libraryTemplates.length === 0 && (
        <Alert severity="info" sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            {t('libraryEmpty.title')}
          </Typography>
          <Typography variant="body2">
            {hasActiveFilters ? t('libraryEmpty.bodyFiltered') : t('libraryEmpty.bodyDefault')}
          </Typography>
        </Alert>
      )}

      {!isLoading && libraryTemplates.length > 0 && (
        <VirtuosoGrid
          useWindowScroll
          totalCount={libraryTemplates.length}
          overscan={600}
          components={gridComponents}
          computeItemKey={(index) => libraryTemplates[index].id}
          itemContent={(index) => {
            const tpl = libraryTemplates[index];
            return (
              <Card sx={{ height: '100%' }}>
                <CardActionArea
                  onClick={() => handleTemplateClick(tpl)}
                  sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      bgcolor: 'grey.50',
                      display: 'flex',
                      justifyContent: 'center',
                      p: 1,
                    }}
                  >
                    {tpl.renderMode === 'chat' ? (
                      <TemplateChatPreview
                        sourceHtml={tpl.sourceHtml ?? ''}
                        brand={libraryPreviewCtx.brand}
                        displayWidth={220}
                        maxDisplayHeight={320}
                      />
                    ) : (
                      <TemplateDocumentPreview
                        document={tpl.document ?? {}}
                        brand={libraryPreviewCtx.brand}
                        pickRecentMedia={libraryPreviewCtx.pickRecentMedia}
                        pickTransparentMedia={libraryPreviewCtx.pickTransparentMedia}
                        pickProduct={libraryPreviewCtx.pickProduct}
                        pickProductRich={libraryPreviewCtx.pickProductRich}
                        mediaOffset={hashStringToOffset(tpl.id)}
                        displayWidth={220}
                        maxDisplayHeight={320}
                        defaultHeader={defaultHeader}
                        defaultFooter={defaultFooter}
                      />
                    )}
                  </Box>
                  <CardContent>
                    <Typography variant="subtitle2" noWrap>
                      {tpl.name}
                    </Typography>
                    {tpl.description && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          mt: 0.5,
                        }}
                      >
                        {tpl.description}
                      </Typography>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            );
          }}
        />
      )}

      {tenantReady && (
        <LibraryTemplateFillDialog
          open={!!fillDialogTemplate}
          onClose={() => setFillDialogTemplate(null)}
          template={fillDialogTemplate}
          orgId={orgId}
          clientId={clientId}
          onSaved={(newId) => {
            setFillDialogTemplate(null);
            navigate({ to: `/dashboard/emails/${newId}/edit` });
          }}
        />
      )}

      {tenantReady && (
        <LibraryChatTemplateFillDialog
          open={!!chatDialogTemplate}
          onClose={() => setChatDialogTemplate(null)}
          template={chatDialogTemplate}
          orgId={orgId}
          clientId={clientId}
          onSaved={(newId) => {
            setChatDialogTemplate(null);
            navigate({ to: `/dashboard/emails/${newId}/chat` });
          }}
        />
      )}

      <UpgradeModal
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        type="generation"
        organizationId={orgId}
        used={quota?.used ?? 0}
        limit={quota?.limit ?? 0}
        nextPlanName={quota?.nextPlanName}
      />
    </>
  );
}

// VirtuosoGrid renders into a `List` element with `Item` wrappers around each
// cell. We give the List a CSS-grid layout with auto-fill columns so the
// number of columns adapts to width the same way the original MUI Grid did,
// and let Item be a transparent wrapper that fills its grid cell. Virtuoso
// keeps offscreen rows unmounted entirely — that's the whole point of the
// switch — so the live preview count stays bounded no matter how many
// templates are in the library.
const GridList = forwardRef<HTMLDivElement, { style?: React.CSSProperties; children?: React.ReactNode }>(
  ({ style, children, ...props }, ref) => (
    <div
      ref={ref}
      {...props}
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
        gap: 24,
        ...style,
      }}
    >
      {children}
    </div>
  ),
);
GridList.displayName = 'TemplateLibraryGridList';

const GridItem = forwardRef<HTMLDivElement, { style?: React.CSSProperties; children?: React.ReactNode }>(
  ({ style, children, ...props }, ref) => (
    <div ref={ref} {...props} style={{ display: 'flex', ...style }}>
      {children}
    </div>
  ),
);
GridItem.displayName = 'TemplateLibraryGridItem';

const gridComponents = {
  List: GridList,
  Item: GridItem,
};

function FilterSelect({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { id: string; label: string }[];
  value: string | null;
  onChange: (next: string | null) => void;
}) {
  const { t } = useTranslation('templates');
  if (options.length === 0) return null;
  return (
    <FormControl size="small" sx={{ minWidth: 180 }}>
      <InputLabel id={`${label}-filter-label`}>{label}</InputLabel>
      <Select
        labelId={`${label}-filter-label`}
        label={label}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value ? e.target.value : null)}
      >
        <MenuItem value="">
          <em>{t('filters.all')}</em>
        </MenuItem>
        {options.map((opt) => (
          <MenuItem key={opt.id} value={opt.id} sx={{ textTransform: 'capitalize' }}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function hashStringToOffset(s: string): number {
  let h = 5381;
  // djb2 hash — bitwise ops are intentional here.
  // eslint-disable-next-line no-bitwise
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}
